import API_BASE_URL from '../config/apiConfig';
import { apiClient } from './authService';

/**
 * Submit a report (e.g., report a landlord/tenant).
 * Endpoint: POST /api/reports
 * @param {Object} reportData - The report data
 * @param {string} reportData.level - 'LANDLORD' | 'TENANT' | 'PROPERTY' | 'UNIT'
 * @param {string} reportData.category - 'BEHAVIOUR' | 'SCAM' | 'MAINTENANCE' | 'OTHER'
 * @param {string} reportData.title - Short title of the report
 * @param {string} reportData.description - Detailed description of the issue
 * @param {string} reportData.targetUserId - The user ID of the person being reported
 * @param {string} [reportData.targetId] - The ID of the specific entity (property/unit) being reported
 * @returns {Promise<Object>} API response
 */
export const createReport = async (reportData) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/reports`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to submit report';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create report error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
/**
 * Get all reports (Admin view).
 * Endpoint: GET /api/reports/admin
 * @param {Object} params - Query parameters
 * @param {string} params.level - 'ADMIN' | 'LANDLORD' | 'TENANT' | 'PROPERTY' | 'UNIT'
 * @param {string} params.status - 'OPEN' | 'RESOLVED' | 'CLOSED'
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.size - Items per page
 * @returns {Promise<Object>} API response
 */
export const getAdminReports = async (params = {}) => {
  const queryParams = new URLSearchParams({
    level: params.level || 'ADMIN',
    status: params.status || 'OPEN',
    page: params.page || 0,
    size: params.size || 20
  });

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/reports/admin?${queryParams}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch reports';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getAdminReports error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};
/**
 * Update report status (Admin only).
 * Endpoint: PUT /api/reports/admin/{reportId}/status
 * @param {string} reportId - ID of the report
 * @param {string} status - 'RESOLVED' | 'CLOSED'
 * @param {string} notes - Resolution notes
 */
export const updateReportStatus = async (reportId, status, notes) => {
  const queryParams = new URLSearchParams({
    status: status,
    resolutionNotes: notes
  });

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/reports/admin/${reportId}/status?${queryParams}`,
      {
        method: 'POST',
      },
      true
    );

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to update report status');
    return data;
  } catch (error) {
    console.error('updateReportStatus error:', error);
    throw error;
  }
};

/**
 * Get reports submitted by the current user.
 * Endpoint: GET /api/reports/me
 * @param {Object} params - { page, size }
 */
export const getMyReports = async ({ page = 0, size = 20 } = {}) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });

  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/reports/me?${queryParams}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Failed to fetch your reports');
    return data;
  } catch (error) {
    console.error('getMyReports error:', error);
    throw error;
  }
};
